import { BindingPath } from "../CoreTypes";
import { PathUtils } from "../PathUtils/PathUtils";
import { isNotNullOrUndefined } from "../../../../../Common/TypingUtils";

import { ICandyComponentHandle } from "./ICandyComponentHandle";

export interface IControlRegistry {
    registerComponentHandle(
        bindingPath: BindingPath,
        control: ICandyComponentHandle,
        exclusiveRegistration?: boolean
    ): void;
    unregisterComponentHandle(bindingPath: BindingPath, control: ICandyComponentHandle): void;
    getComponentHandles(bindingPath: BindingPath): undefined | readonly ICandyComponentHandle[];
    getBindingPaths(): Iterable<string>;
}

interface HandleListItem {
    control: ICandyComponentHandle;
    isExclusive: boolean;
}

export class ControlRegistry implements IControlRegistry {
    private readonly controls: Map<string, HandleListItem[]> = new Map();

    public registerComponentHandle(
        bindingPath: BindingPath,
        control: ICandyComponentHandle,
        exclusiveRegistration?: boolean
    ): void {
        const normalizedId = this.getNormalizedId(bindingPath);

        if (exclusiveRegistration) {
            this.controls.set(normalizedId, [{ control: control, isExclusive: true }]);
            return;
        }

        if (!this.controls.has(normalizedId)) {
            this.controls.set(normalizedId, [{ control: control, isExclusive: false }]);
            return;
        }

        const { index } = this.find(normalizedId, x => x.isExclusive === true);
        if (index > -1) {
            return;
        }

        this.getHandleList(normalizedId)?.push({ control: control, isExclusive: false });
    }

    public unregisterComponentHandle(bindingPath: BindingPath, control: ICandyComponentHandle): void {
        const normalizedId = this.getNormalizedId(bindingPath);

        const { handleList, index } = this.find(normalizedId, x => x.control === control);
        if (index > -1) {
            handleList.splice(index, 1);
        }
        if (handleList.length === 0) {
            this.controls.delete(normalizedId);
        }
    }

    public getBindingPaths(): Iterable<string> {
        return this.controls.keys();
    }

    public getComponentHandles(bindingPath: BindingPath): readonly ICandyComponentHandle[] | undefined {
        let path: string | null = bindingPath;
        while (isNotNullOrUndefined(path)) {
            //NOTE: почему-то здесь не нормализуется bindingPath сначала
            const handleList = this.getHandleList(bindingPath)?.map(x => x.control);
            if (isNotNullOrUndefined(handleList)) {
                return handleList;
            } else {
                path = PathUtils.getParentPath(path);
            }
        }

        return undefined;
    }

    private find(
        bindingPath: string,
        predicate: (x: HandleListItem) => boolean
    ): { handleList: HandleListItem[]; index: number } {
        const handleList = this.getHandleList(bindingPath);
        if (handleList == undefined) {
            return { handleList: [], index: -1 };
        }
        const index = handleList.findIndex(predicate);
        if (index > -1) {
            return { handleList: handleList, index: index };
        }
        return { handleList: handleList, index: -1 };
    }

    private getNormalizedId(bindingPath: string): string {
        return bindingPath.replace(/\/\*$/g, "");
    }

    private getHandleList(bindingPath: string): HandleListItem[] | undefined {
        return this.controls.get(bindingPath);
    }
}
